import { useState, useEffect, useMemo } from 'react';
import { API, getActiveBackendUrl } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Copy,
  RefreshCw,
  Search,
  X,
  Eye,
  DollarSign,
  Clock,
  Car,
  Calendar,
  TrendingUp,
  FileText,
  Upload,
  CheckSquare,
  Square,
  Download,
  FolderOpen,
  KeyRound,
  Euro,
  AlertCircle,
  Edit,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';


export default function PartnerManagement({ token }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerRides, setPartnerRides] = useState([]);
  const [loadingRides, setLoadingRides] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get backend URL lazily to avoid initialization order issues
  const BACKEND_URL = useMemo(() => getActiveBackendUrl(), []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    mymaps_url: '',
    send_invite: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [syncingPartnerId, setSyncingPartnerId] = useState(null);
  
  // New state for admin payment marking
  const [selectedRidesForPayment, setSelectedRidesForPayment] = useState([]);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  
  // State for partner uploaded files/invoices
  const [partnerInvoices, setPartnerInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedPartnerForInvoices, setSelectedPartnerForInvoices] = useState(null);

  // Payment modal filters: month + unpaid-only toggle
  const [monthFilter, setMonthFilter] = useState('all'); // 'all' or 'YYYY-MM'
  const [unpaidOnly, setUnpaidOnly] = useState(false);

  // Lump-sum payment + Complaints + Credit
  const [showLumpSumModal, setShowLumpSumModal] = useState(false);
  const [lumpSumAmount, setLumpSumAmount] = useState('');
  const [lumpSumNotes, setLumpSumNotes] = useState('');
  const [submittingLumpSum, setSubmittingLumpSum] = useState(false);
  const [lumpSumHistory, setLumpSumHistory] = useState([]);
  const [loadingLumpSums, setLoadingLumpSums] = useState(false);
  const [editingLumpSum, setEditingLumpSum] = useState(null); // { id, notes }
  const [showComplaintsModal, setShowComplaintsModal] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [partnerFinance, setPartnerFinance] = useState({ credit_balance: 0, complaints_total: 0 });
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [complaintForm, setComplaintForm] = useState({ description: '', amount: '', date: '', ride_id: '', status: 'pending' });
  const [savingComplaint, setSavingComplaint] = useState(false);

  useEffect(() => {
    loadPartners();
  }, [token]);

  // When the month filter changes while a partner's payment modal is open,
  // refresh the lump-sum history so it only shows lumps for that month.
  useEffect(() => {
    if (selectedPartner) {
      fetchLumpSums(selectedPartner.id, monthFilter);
    }
  }, [monthFilter, selectedPartner]);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error loading partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to download invoice with authentication
  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoice.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('File non disponibile. Il partner deve ricaricare la fattura.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.file_name || 'invoice';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(error.message || 'Error downloading invoice');
    }
  };

  // Helper to download auto-generated Settlement Excel snapshot for a payment record
  const handleDownloadSettlementExcel = async (invoice) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoice.id}/settlement-excel`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Download failed: ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = invoice.settlement_excel_filename || `Settlement_${invoice.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Settlement Excel downloaded');
    } catch (error) {
      console.error('Error downloading settlement excel:', error);
      toast.error(error.message || 'Error downloading settlement Excel');
    }
  };

  // Helper function to preview invoice with authentication
  const handlePreviewInvoice = async (invoice) => {
    try {
      const response = await fetch(`${API}/admin/invoices/${invoice.id}/preview`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('File non disponibile. Il partner deve ricaricare la fattura.');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Preview failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Open in new tab for preview
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing invoice:', error);
      toast.error(error.message || 'Error viewing invoice');
    }
  };

  // Load invoices uploaded by a specific partner
  const loadPartnerInvoices = async (partner) => {
    try {
      setLoadingInvoices(true);
      setSelectedPartnerForInvoices(partner);
      setShowInvoicesModal(true);
      
      const response = await axios.get(`${API}/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter invoices for this specific partner
      const partnerInvoiceList = response.data.filter(inv => inv.partner_id === partner.id);
      setPartnerInvoices(partnerInvoiceList);
    } catch (error) {
      console.error('Error loading partner invoices:', error);
      toast.error('Failed to load partner invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/partners`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner created successfully');
      
      // Show invite link
      if (response.data.invite_link) {
        setInviteLink(response.data.invite_link);
        setShowInviteDialog(true);
      }
      
      setShowAddModal(false);
      resetForm();
      loadPartners();
    } catch (error) {
      console.error('Error creating partner:', error);
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePartner = async (e) => {
    e.preventDefault();
    if (!selectedPartner) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/partners/${selectedPartner.id}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        mymaps_url: formData.mymaps_url || null,
        is_active: formData.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner updated successfully');
      setShowEditModal(false);
      resetForm();
      loadPartners();
    } catch (error) {
      console.error('Error updating partner:', error);
      toast.error(error.response?.data?.detail || 'Failed to update partner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner) return;
    
    try {
      await axios.delete(`${API}/partners/${selectedPartner.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner deleted successfully');
      setShowDeleteDialog(false);
      setSelectedPartner(null);
      loadPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete partner');
    }
  };

  const handleResendInvite = async (partner) => {
    try {
      const response = await axios.post(`${API}/partners/${partner.id}/resend-invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.invite_link) {
        setInviteLink(response.data.invite_link);
        setShowInviteDialog(true);
      }
      
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      mymaps_url: '',
      send_invite: true
    });
    setSelectedPartner(null);
  };

  const openEditModal = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email,
      phone: partner.phone || '',
      company: partner.company || '',
      mymaps_url: partner.mymaps_url || '',
      is_active: partner.is_active
    });
    setShowEditModal(true);
  };

  const handleSyncPartnerAreas = async (partner) => {
    if (!partner?.mymaps_url) {
      toast.error('This partner has no MyMaps URL set. Edit the partner and paste the link first.');
      return;
    }
    setSyncingPartnerId(partner.id);
    try {
      const res = await axios.post(
        `${API}/pricing-areas/sync-from-partner/${partner.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { created = 0, updated = 0, skipped = 0, total_found = 0 } = res.data || {};
      toast.success(`Sync done · ${created} new · ${updated} updated · ${skipped} skipped (of ${total_found})`);
      loadPartners();
    } catch (error) {
      console.error('Error syncing MyMaps:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync MyMaps');
    } finally {
      setSyncingPartnerId(null);
    }
  };

  const openDeleteDialog = (partner) => {
    setSelectedPartner(partner);
    setShowDeleteDialog(true);
  };

  const openResetPasswordDialog = (partner) => {
    setSelectedPartner(partner);
    setResetPasswordResult(null);
    setShowResetPasswordDialog(true);
  };

  const handleResetPartnerPassword = async () => {
    if (!selectedPartner?.id) return;
    setResettingPassword(true);
    try {
      const response = await axios.post(
        `${API}/admin/reset-partner-password`,
        { partner_id: selectedPartner.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetPasswordResult(response.data);
      toast.success('Password resettata con successo');
    } catch (error) {
      console.error('Error resetting partner password:', error);
      toast.error(error.response?.data?.detail || 'Error resetting password');
    } finally {
      setResettingPassword(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const openPaymentModal = async (partner) => {
    setSelectedPartner(partner);
    setShowPaymentModal(true);
    setLoadingRides(true);
    setMonthFilter('all');
    setUnpaidOnly(false);
    
    try {
      // Server-side filter by partner_id (matches partner_id OR partner text against name/company)
      const response = await axios.get(`${API}/rides?include_past=true&partner_id=${encodeURIComponent(partner.id)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerRides(response.data);
      // Load finance summary + complaints + lump-sum history in parallel
      fetchPartnerFinance(partner.id);
      fetchComplaints(partner.id);
      fetchLumpSums(partner.id, monthFilter);
    } catch (error) {
      console.error('Error loading partner rides:', error);
      toast.error('Failed to load partner rides');
    } finally {
      setLoadingRides(false);
    }
  };

  // Export partner payments to Excel (requires a specific month)
  const handleExportExcel = async () => {
    if (!selectedPartner) return;
    if (!monthFilter || monthFilter === 'all') {
      toast.error('Select a specific month before exporting');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('month', monthFilter);
      if (unpaidOnly) params.append('unpaid_only', 'true');
      const url = `${API}/partners/${selectedPartner.id}/payments/export-excel?${params.toString()}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      // Trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      // Try to read filename from header
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `PartnerPayments_${selectedPartner.name || 'partner'}_${monthFilter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  // Helper: month key from date string (YYYY-MM)
  const monthKey = (dateStr) => {
    if (!dateStr) return 'unknown';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'unknown';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  // Helper: human-readable month label (e.g. "May 2026")
  const monthLabel = (key) => {
    if (key === 'unknown') return 'Unscheduled';
    const [y, m] = key.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  // Available months from partnerRides (sorted desc)
  const availableMonths = useMemo(() => {
    const set = new Set();
    partnerRides.forEach(r => set.add(monthKey(r.date_time)));
    return Array.from(set).sort().reverse();
  }, [partnerRides]);

// Rides we owe the partner for: completed + customer/driver no-shows (still payable)
const PAYABLE_STATUSES = ['completed', 'no_show', 'driver_no_show'];
const isPayable = (r) => PAYABLE_STATUSES.includes(r.status);

  // Filtered rides (by month + unpaid toggle)
  const filteredRides = useMemo(() => {
    return partnerRides.filter(r => {
      if (monthFilter !== 'all' && monthKey(r.date_time) !== monthFilter) return false;
      if (unpaidOnly && !(isPayable(r) && !r.is_paid)) return false;
      return true;
    });
  }, [partnerRides, monthFilter, unpaidOnly]);

  // Complaints scoped to the selected month: a complaint dated in May must NOT
  // affect June's payment summary. Only "pending" and "resolved" complaints
  // still reduce what we owe (refunded ones are already returned to the partner).
  const filteredComplaintsTotal = useMemo(() => {
    return complaints.reduce((sum, c) => {
      if (c.status === 'refunded') return sum;
      if (monthFilter !== 'all' && monthKey(c.date) !== monthFilter) return sum;
      return sum + (parseFloat(c.amount) || 0);
    }, 0);
  }, [complaints, monthFilter]);

  // Calculate partner payment statistics (operates on filteredRides — month + unpaid filters apply)
  const getPaymentStats = () => {
    const completedRides = filteredRides.filter(isPayable);
    const unpaidRides = completedRides.filter(r => !r.is_paid);
    const paidRides = completedRides.filter(r => r.is_paid);
    
    const getPartnerAmount = (r) => r.payment_to_partner || 0;
    const getCustomerPrice = (r) => r.price || 0;
    
    return {
      totalRides: filteredRides.length,
      completedRides: completedRides.length,
      unpaidCount: unpaidRides.length,
      paidCount: paidRides.length,
      // Customer prices
      totalCustomerPrice: completedRides.reduce((sum, r) => sum + getCustomerPrice(r), 0),
      paidCustomerPrice: paidRides.reduce((sum, r) => sum + getCustomerPrice(r), 0),
      unpaidCustomerPrice: unpaidRides.reduce((sum, r) => sum + getCustomerPrice(r), 0),
      // Partner payments
      totalPartnerPayment: completedRides.reduce((sum, r) => sum + getPartnerAmount(r), 0),
      paidPartnerPayment: paidRides.reduce((sum, r) => sum + getPartnerAmount(r), 0),
      unpaidPartnerPayment: unpaidRides.reduce((sum, r) => sum + getPartnerAmount(r), 0),
    };
  };

  const stats = showPaymentModal ? getPaymentStats() : {};
  // Extra cash already in the partner's hands via lump-sum payments in current
  // month scope but not yet allocated to a ride (credit_left portion).
  const lumpSumCreditExtra = useMemo(() => {
    if (!lumpSumHistory || lumpSumHistory.length === 0) return 0;
    return lumpSumHistory.reduce((sum, ls) => sum + (ls.credit_left || 0), 0);
  }, [lumpSumHistory]);

  // Toggle ride selection for payment
  const toggleRideSelection = (rideId) => {
    setSelectedRidesForPayment(prev => {
      if (prev.includes(rideId)) {
        return prev.filter(id => id !== rideId);
      } else {
        return [...prev, rideId];
      }
    });
  };

  // Select all unpaid rides (within the current filter view)
  const selectAllUnpaidRides = () => {
    const unpaidRideIds = filteredRides
      .filter(r => isPayable(r) && !r.is_paid)
      .map(r => r.id);
    setSelectedRidesForPayment(unpaidRideIds);
  };

  // Clear all selections
  const clearRideSelection = () => {
    setSelectedRidesForPayment([]);
  };

  // Get selected rides total amount
  const getSelectedRidesTotal = () => {
    return partnerRides
      .filter(r => selectedRidesForPayment.includes(r.id))
      .reduce((sum, r) => sum + (r.payment_to_partner || 0), 0);
  };

  // Handle marking rides as paid
  const handleMarkRidesAsPaid = async () => {
    if (selectedRidesForPayment.length === 0) {
      toast.error('Please select at least one ride');
      return;
    }

    setMarkingPaid(true);
    try {
      const formData = new FormData();
      formData.append('ride_ids', JSON.stringify(selectedRidesForPayment));
      formData.append('partner_id', selectedPartner.id);
      // Receipt file is optional
      if (receiptFile) {
        formData.append('receipt_file', receiptFile);
      }
      if (paymentNotes) {
        formData.append('notes', paymentNotes);
      }

      await axios.post(`${API}/admin/partner-payments/mark-paid`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`${selectedRidesForPayment.length} ride(s) marked as paid`);
      
      // Reset state and reload rides
      setShowMarkPaidModal(false);
      setReceiptFile(null);
      setPaymentNotes('');
      setSelectedRidesForPayment([]);
      
      // Reload partner rides
      openPaymentModal(selectedPartner);
    } catch (error) {
      console.error('Error marking rides as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark rides as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  // -------------- Lump-sum + Complaints --------------
  const fetchPartnerFinance = async (partnerId) => {
    if (!partnerId) return;
    try {
      const r = await axios.get(`${API}/admin/partner-finance/summary/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartnerFinance(r.data || { credit_balance: 0, complaints_total: 0 });
    } catch {
      setPartnerFinance({ credit_balance: 0, complaints_total: 0 });
    }
  };

  const fetchLumpSums = async (partnerId, month) => {
    if (!partnerId) return;
    setLoadingLumpSums(true);
    try {
      const url = `${API}/admin/partner-payments/lump-sum?partner_id=${partnerId}` +
        (month && month !== 'all' ? `&month=${month}` : '');
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLumpSumHistory(r.data || []);
    } catch (err) {
      // Silent — list is secondary info
      setLumpSumHistory([]);
    } finally {
      setLoadingLumpSums(false);
    }
  };

  const saveLumpSumNotes = async () => {
    if (!editingLumpSum) return;
    try {
      await axios.patch(
        `${API}/admin/partner-payments/lump-sum/${editingLumpSum.id}`,
        { notes: editingLumpSum.notes || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes updated');
      setEditingLumpSum(null);
      fetchLumpSums(selectedPartner.id, monthFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update notes');
    }
  };

  const deleteLumpSum = async (ls) => {
    const msg = `Delete this lump-sum payment of €${ls.amount_input.toFixed(2)}?\n\n` +
      `• ${ls.ride_ids?.length || 0} ride(s) will go back to unpaid\n` +
      `• Credit balance will be adjusted (refund €${ls.credit_used.toFixed(2)}, remove €${ls.credit_left.toFixed(2)})`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/admin/partner-payments/lump-sum/${ls.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lump-sum payment deleted');
      // Refresh everything
      openPaymentModal(selectedPartner);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const fetchComplaints = async (partnerId) => {
    if (!partnerId) return;
    setLoadingComplaints(true);
    try {
      const r = await axios.get(`${API}/admin/partner-complaints?partner_id=${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setComplaints(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load complaints');
    } finally {
      setLoadingComplaints(false);
    }
  };

  const submitLumpSum = async () => {
    const amt = parseFloat(lumpSumAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmittingLumpSum(true);
    try {
      const fd = new FormData();
      fd.append('partner_id', selectedPartner.id);
      fd.append('amount', String(amt));
      if (lumpSumNotes) fd.append('notes', lumpSumNotes);
      if (monthFilter && monthFilter !== 'all') fd.append('month', monthFilter);
      const r = await axios.post(`${API}/admin/partner-payments/lump-sum`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const data = r.data;
      const msg = data.rides_paid_count > 0
        ? `Paid ${data.rides_paid_count} ride(s) for €${data.rides_paid_total.toFixed(2)}` + (data.credit_left > 0 ? ` — €${data.credit_left.toFixed(2)} credit saved for next payment.` : '')
        : `€${amt.toFixed(2)} saved as credit (anticipo).`;
      toast.success(msg);
      setLumpSumAmount('');
      setLumpSumNotes('');
      // Refresh rides + finance + history (modal stays open so admin sees the new entry).
      const partnerId = selectedPartner.id;
      try {
        const ridesResp = await axios.get(`${API}/rides?include_past=true&partner_id=${encodeURIComponent(partnerId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPartnerRides(ridesResp.data);
      } catch {}
      fetchPartnerFinance(partnerId);
      fetchLumpSums(partnerId, monthFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lump sum failed');
    } finally {
      setSubmittingLumpSum(false);
    }
  };

  const openComplaintForm = (c = null) => {
    if (c) {
      setEditingComplaint(c);
      setComplaintForm({
        description: c.description || '',
        amount: String(c.amount || ''),
        date: c.date || '',
        ride_id: c.ride_id || '',
        status: c.status || 'pending',
      });
    } else {
      setEditingComplaint(null);
      const today = new Date().toISOString().slice(0, 10);
      setComplaintForm({ description: '', amount: '', date: today, ride_id: '', status: 'pending' });
    }
  };

  const saveComplaint = async () => {
    const amt = parseFloat(complaintForm.amount);
    if (!complaintForm.description.trim() || !amt || amt <= 0) {
      toast.error('Description and a positive amount are required');
      return;
    }
    setSavingComplaint(true);
    try {
      const body = {
        description: complaintForm.description.trim(),
        amount: amt,
        date: complaintForm.date || undefined,
        ride_id: complaintForm.ride_id || undefined,
        status: complaintForm.status,
      };
      if (editingComplaint) {
        await axios.put(`${API}/admin/partner-complaints/${editingComplaint.id}`, body, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Complaint updated');
      } else {
        await axios.post(`${API}/admin/partner-complaints`, { ...body, partner_id: selectedPartner.id }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Complaint added');
      }
      setEditingComplaint(null);
      setComplaintForm({ description: '', amount: '', date: '', ride_id: '', status: 'pending' });
      fetchComplaints(selectedPartner.id);
      fetchPartnerFinance(selectedPartner.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save complaint');
    } finally {
      setSavingComplaint(false);
    }
  };

  const deleteComplaint = async (c) => {
    if (!window.confirm(`Delete complaint of €${c.amount.toFixed(2)}?`)) return;
    try {
      await axios.delete(`${API}/admin/partner-complaints/${c.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Complaint deleted');
      fetchComplaints(selectedPartner.id);
      fetchPartnerFinance(selectedPartner.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const filteredPartners = partners.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.company || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-sky-500" />
            Partner Management
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage partners who can access the Partner Portal
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-sky-500 hover:bg-sky-600"
          data-testid="add-partner-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search partners..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 max-w-md"
          data-testid="partner-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Partners List */}
      {filteredPartners.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-sm">
          <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">
            {searchQuery ? 'No partners match your search' : 'No partners yet'}
          </p>
          {!searchQuery && (
            <Button
              onClick={() => setShowAddModal(true)}
              variant="outline"
              className="mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add your first partner
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredPartners.map(partner => (
            <div
              key={partner.id}
              className="bg-white border rounded-sm p-4 hover:shadow-md transition-shadow"
              data-testid={`partner-card-${partner.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 lg:gap-4 flex-1 min-w-0">
                  <div className={`h-10 w-10 lg:h-12 lg:w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    partner.is_active ? 'bg-sky-100' : 'bg-gray-100'
                  }`}>
                    <Building2 className={`h-5 w-5 lg:h-6 lg:w-6 ${
                      partner.is_active ? 'text-sky-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-base lg:text-lg truncate">{partner.name}</h3>
                      {partner.is_active ? (
                        <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 text-xs">Inactive</Badge>
                      )}
                    </div>
                    {partner.company && (
                      <p className="text-sm text-gray-600 truncate">{partner.company}</p>
                    )}
                    <div className="flex flex-col lg:flex-row lg:flex-wrap gap-1 lg:gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{partner.email}</span>
                      </span>
                      {partner.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          {partner.phone}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Created: {formatDate(partner.created_at)}
                    </p>
                  </div>
                </div>
                {/* Desktop Actions */}
                <div className="hidden lg:flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPaymentModal(partner)}
                    title="View Payments"
                    className="text-sky-600 hover:bg-sky-50"
                    data-testid={`view-payments-${partner.id}`}
                  >
                    <DollarSign className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadPartnerInvoices(partner)}
                    title="View Uploaded Files"
                    className="text-emerald-600 hover:bg-emerald-50"
                    data-testid={`view-invoices-${partner.id}`}
                  >
                    <FolderOpen className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendInvite(partner)}
                    title="Resend Invite"
                    data-testid={`resend-invite-${partner.id}`}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(partner)}
                    data-testid={`edit-partner-${partner.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {partner.mymaps_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncPartnerAreas(partner)}
                      className="text-blue-600 hover:bg-blue-50"
                      title="Sync areas from partner's MyMaps"
                      disabled={syncingPartnerId === partner.id}
                      data-testid={`sync-mymaps-${partner.id}`}
                    >
                      <Globe className={`h-4 w-4 ${syncingPartnerId === partner.id ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openResetPasswordDialog(partner)}
                    className="text-amber-600 hover:bg-amber-50"
                    title="Reset Password"
                    data-testid={`reset-password-partner-${partner.id}`}
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(partner)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`delete-partner-${partner.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Mobile Actions */}
              <div className="lg:hidden flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openPaymentModal(partner)}
                  className="text-sky-600 hover:bg-sky-50 h-9 px-3"
                >
                  <DollarSign className="h-4 w-4 mr-1" />
                  <span className="text-xs">Pay</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadPartnerInvoices(partner)}
                  className="text-emerald-600 hover:bg-emerald-50 h-9 px-3"
                >
                  <FolderOpen className="h-4 w-4 mr-1" />
                  <span className="text-xs">Files</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(partner)}
                  className="h-9 px-3"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openResetPasswordDialog(partner)}
                  className="text-amber-600 hover:bg-amber-50 h-9 px-3"
                  title="Reset Password"
                >
                  <KeyRound className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openDeleteDialog(partner)}
                  className="text-red-600 hover:bg-red-50 h-9 px-3"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Partner Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-sky-500" />
              Add New Partner
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPartner}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Partner name"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="partner@company.com"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+30 210 555 0000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="mymaps_url">Google MyMaps link <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
                <Input
                  id="mymaps_url"
                  value={formData.mymaps_url}
                  onChange={(e) => setFormData({ ...formData, mymaps_url: e.target.value })}
                  placeholder="https://www.google.com/maps/d/embed?mid=..."
                  className="mt-1"
                  noUpperCase
                  data-testid="partner-mymaps-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  If the partner provides a MyMaps link with their zones, paste it here to enable one-click area sync later.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="send_invite">Send Invite Email</Label>
                  <p className="text-xs text-gray-500">Email invite link to partner</p>
                </div>
                <Switch
                  id="send_invite"
                  checked={formData.send_invite}
                  onCheckedChange={(checked) => setFormData({ ...formData, send_invite: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-sky-500 hover:bg-sky-600">
                {submitting ? 'Creating...' : 'Create Partner'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-sky-500" />
              Edit Partner
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePartner}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-company">Company</Label>
                <Input
                  id="edit-company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-mymaps-url">Google MyMaps link <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
                <Input
                  id="edit-mymaps-url"
                  value={formData.mymaps_url}
                  onChange={(e) => setFormData({ ...formData, mymaps_url: e.target.value })}
                  placeholder="https://www.google.com/maps/d/embed?mid=..."
                  className="mt-1"
                  noUpperCase
                  data-testid="edit-partner-mymaps-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  When set, you can re-import the partner&apos;s zones with one click from the partner card.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is_active">Active</Label>
                  <p className="text-xs text-gray-500">Partner can access portal</p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-sky-500 hover:bg-sky-600">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedPartner?.name}</strong>? This action cannot be undone and will remove their access to the Partner Portal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedPartner(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePartner} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={(open) => { setShowResetPasswordDialog(open); if (!open) { setResetPasswordResult(null); setSelectedPartner(null); } }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-500" />
              Reset Password Partner
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {resetPasswordResult ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium text-sm mb-2">Password resettata con successo!</p>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-slate-500">Partner</p>
                      <p className="font-medium text-sm">{resetPasswordResult.partner_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-sm">{resetPasswordResult.partner_email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Password Temporanea</p>
                      <div className="flex items-center gap-2">
                        <code className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded font-mono text-sm font-bold">
                          {resetPasswordResult.temporary_password}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(resetPasswordResult.temporary_password);
                            toast.success('Password copiata!');
                          }}
                          data-testid="copy-temp-password-btn"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Comunica questa password temporanea al partner. Dovrebbe cambiarla dopo il primo accesso.
                </p>
                <Button onClick={() => { setShowResetPasswordDialog(false); setResetPasswordResult(null); }} className="w-full">
                  Chiudi
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Stai per generare una nuova password temporanea per <strong>{selectedPartner?.company_name || selectedPartner?.name}</strong>.
                </p>
                <p className="text-sm text-slate-500">
                  Email: <span className="font-medium">{selectedPartner?.email}</span>
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 text-xs">
                    La password attuale verrà sostituita. Il partner dovrà usare la nuova password temporanea per accedere.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResetPartnerPassword}
                    disabled={resettingPassword}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="confirm-reset-password-btn"
                  >
                    {resettingPassword ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <KeyRound className="h-4 w-4 mr-2" />
                    )}
                    Genera Password
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-sky-500" />
              Partner Invite Link
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Share this link with the partner to complete their registration:
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={inviteLink}
                readOnly
                className="bg-gray-50"
              />
              <Button onClick={copyInviteLink} variant="outline">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInviteDialog(false)} className="bg-sky-500 hover:bg-sky-600">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Payments Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => { setShowPaymentModal(open); if (!open) { setSelectedPartner(null); setPartnerRides([]); setMonthFilter('all'); setUnpaidOnly(false); setSelectedRidesForPayment([]); } }}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-sky-500" />
              Payment Details - {selectedPartner?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingRides ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto py-4">
              {/* Filter Bar: Month selector + Unpaid-only toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <div className="flex items-center gap-2 flex-1">
                  <Calendar className="h-4 w-4 text-slate-500" />
                  <Label className="text-xs text-slate-600 whitespace-nowrap">Month:</Label>
                  <Select value={monthFilter} onValueChange={setMonthFilter}>
                    <SelectTrigger className="h-8 text-xs w-full sm:w-[180px]" data-testid="payment-month-filter">
                      <SelectValue placeholder="All months" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All months</SelectItem>
                      {availableMonths.map(m => (
                        <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="unpaid-only-toggle"
                    checked={unpaidOnly}
                    onCheckedChange={setUnpaidOnly}
                    data-testid="payment-unpaid-only-toggle"
                  />
                  <Label htmlFor="unpaid-only-toggle" className="text-xs cursor-pointer">
                    Unpaid only
                  </Label>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportExcel}
                  className="text-xs h-8 gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="payment-export-excel-btn"
                  disabled={partnerRides.length === 0 || monthFilter === 'all'}
                  title={monthFilter === 'all' ? 'Select a specific month to enable export' : 'Export filtered rides to Excel'}
                >
                  <Download className="h-3 w-3" />
                  Export Excel
                </Button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div className="bg-blue-50 rounded-sm p-3 border border-blue-200">
                  <p className="text-xs text-gray-600">Total Rides</p>
                  <p className="text-xl font-bold">{stats.totalRides || 0}</p>
                  <p className="text-xs text-gray-500">{stats.completedRides || 0} payable</p>
                </div>
                <div className="bg-orange-50 rounded-sm p-3 border border-orange-200">
                  <p className="text-xs text-gray-600">Unpaid to Partner</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(Math.max(0, (stats.unpaidPartnerPayment || 0) - (partnerFinance.credit_balance || 0) - (filteredComplaintsTotal || 0)))}
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats.unpaidCount || 0} rides
                    {(partnerFinance.credit_balance > 0 || filteredComplaintsTotal > 0) && (
                      <span className="text-slate-500"> · gross {formatCurrency(stats.unpaidPartnerPayment)}</span>
                    )}
                  </p>
                </div>
                <div className="bg-green-50 rounded-sm p-3 border border-green-200">
                  <p className="text-xs text-gray-600">Paid to Partner</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency((stats.paidPartnerPayment || 0) + lumpSumCreditExtra)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats.paidCount || 0} rides{lumpSumCreditExtra > 0 && (
                      <span className="text-green-700"> · incl. {formatCurrency(lumpSumCreditExtra)} credit paid</span>
                    )}
                  </p>
                </div>
                <div className="bg-sky-50 rounded-sm p-3 border border-sky-200">
                  <p className="text-xs text-gray-600">Company Revenue</p>
                  <p className="text-xl font-bold text-sky-600">{formatCurrency((stats.totalCustomerPrice || 0) - (stats.totalPartnerPayment || 0))}</p>
                  <p className="text-xs text-gray-500">Customer price − Partner payment</p>
                </div>
              </div>

              {/* Deductions row: each card shown independently only when > 0 */}
              {(partnerFinance.credit_balance > 0 || filteredComplaintsTotal > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {partnerFinance.credit_balance > 0 && (
                    <div
                      className="rounded-sm p-3 border bg-violet-50 border-violet-200"
                      data-testid="partner-credit-card"
                    >
                      <p className="text-xs text-gray-600">Credit (advance)</p>
                      <p className="text-xl font-bold text-violet-700">−{formatCurrency(partnerFinance.credit_balance)}</p>
                      <p className="text-xs text-gray-500">Carried over to next settlement</p>
                    </div>
                  )}
                  {filteredComplaintsTotal > 0 && (
                    <button
                      type="button"
                      onClick={() => { setShowComplaintsModal(true); openComplaintForm(null); }}
                      className="text-left rounded-sm p-3 border transition bg-rose-50 border-rose-200 hover:bg-rose-100"
                      data-testid="partner-complaints-card"
                    >
                      <p className="text-xs text-gray-600">
                        Complaints{monthFilter !== 'all' && <span className="text-gray-400"> · {monthLabel(monthFilter)}</span>}
                      </p>
                      <p className="text-xl font-bold text-rose-700">−{formatCurrency(filteredComplaintsTotal)}</p>
                      <p className="text-xs text-gray-500">Tap to view / add</p>
                    </button>
                  )}
                </div>
              )}

              {/* Comparison Table Header */}
              <div className="mb-4 p-3 bg-gray-100 rounded-sm">
                <h4 className="font-semibold text-sm mb-2">Price vs Payment Comparison</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Total Customer Price:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(stats.totalCustomerPrice)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Partner Payment:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(stats.totalPartnerPayment)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Margin:</span>
                    <span className="ml-2 font-semibold text-green-600">
                      {formatCurrency((stats.totalCustomerPrice || 0) - (stats.totalPartnerPayment || 0))}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rides List */}
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Rides ({filteredRides.length})
                </h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowComplaintsModal(true); openComplaintForm(null); }}
                    className="text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="open-complaints-btn"
                  >
                    Complaints {filteredComplaintsTotal > 0 && <span className="ml-1">(−{formatCurrency(filteredComplaintsTotal)})</span>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setLumpSumAmount(''); setLumpSumNotes(''); setShowLumpSumModal(true); }}
                    className="text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="open-lump-sum-btn"
                  >
                    Lump Sum Payment
                  </Button>
                  {filteredRides.filter(r => isPayable(r) && !r.is_paid).length > 0 && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={selectAllUnpaidRides}
                        className="text-xs"
                      >
                        Select All Unpaid
                      </Button>
                      {selectedRidesForPayment.length > 0 && (
                        <>
                          <span
                            className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
                            data-testid="selected-rides-total"
                          >
                            Selected: {formatCurrency(getSelectedRidesTotal())}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={clearRideSelection}
                            className="text-xs"
                          >
                            Clear
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setShowMarkPaidModal(true)}
                            className="text-xs bg-green-600 hover:bg-green-700"
                            data-testid="open-mark-paid-modal"
                          >
                            <Upload className="h-3 w-3 mr-1" />
                            Mark {selectedRidesForPayment.length} as Paid · {formatCurrency(getSelectedRidesTotal())}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {filteredRides.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Car className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>{partnerRides.length === 0 ? 'No rides assigned to this partner yet' : 'No rides match the current filters'}</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                  {(() => {
                    // Group filteredRides by month
                    const sorted = [...filteredRides].sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
                    const groups = {};
                    sorted.forEach(r => {
                      const key = monthKey(r.date_time);
                      if (!groups[key]) groups[key] = [];
                      groups[key].push(r);
                    });
                    const orderedKeys = Object.keys(groups).sort().reverse();
                    return orderedKeys.map(key => {
                      const rides = groups[key];
                      const monthCompleted = rides.filter(isPayable);
                      const monthUnpaid = monthCompleted.filter(r => !r.is_paid);
                      const monthUnpaidTotal = monthUnpaid.reduce((s, r) => s + (r.payment_to_partner || 0), 0);
                      const monthPaidTotal = monthCompleted.filter(r => r.is_paid).reduce((s, r) => s + (r.payment_to_partner || 0), 0);
                      return (
                        <div key={key} data-testid={`month-group-${key}`}>
                          {/* Month header (sticky) */}
                          <div className="sticky top-0 z-10 bg-slate-100 border border-slate-300 rounded-sm px-3 py-2 mb-2 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-slate-600" />
                              <span className="font-semibold text-sm text-slate-800">{monthLabel(key)}</span>
                              <Badge variant="outline" className="text-xs">{rides.length} rides</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              {monthUnpaid.length > 0 && (
                                <span className="text-orange-600 font-medium">
                                  Unpaid: {formatCurrency(monthUnpaidTotal)} ({monthUnpaid.length})
                                </span>
                              )}
                              {monthPaidTotal > 0 && (
                                <span className="text-green-600 font-medium">
                                  Paid: {formatCurrency(monthPaidTotal)}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Rides in month */}
                          <div className="space-y-2">
                            {rides.map(ride => (
                              <div
                                key={ride.id}
                                className={`border rounded-sm p-3 text-sm ${
                                  ride.is_paid ? 'bg-green-50 border-green-200' : 
                                  selectedRidesForPayment.includes(ride.id) ? 'bg-blue-50 border-blue-300' : 'bg-white'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  {/* Checkbox for unpaid payable rides (completed + no-shows) */}
                                  {isPayable(ride) && !ride.is_paid && (
                                    <button
                                      onClick={() => toggleRideSelection(ride.id)}
                                      className="mr-3 mt-1 text-gray-500 hover:text-blue-600"
                                      data-testid={`select-ride-${ride.id}`}
                                    >
                                      {selectedRidesForPayment.includes(ride.id) ? (
                                        <CheckSquare className="h-5 w-5 text-blue-600" />
                                      ) : (
                                        <Square className="h-5 w-5" />
                                      )}
                                    </button>
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Calendar className="h-3 w-3 text-gray-400" />
                                      <span className="font-medium">{formatDateTime(ride.date_time)}</span>
                                      <Badge className={
                                        ride.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        ride.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                        (ride.status === 'no_show' || ride.status === 'driver_no_show') ? 'bg-amber-100 text-amber-800' :
                                        'bg-blue-100 text-blue-800'
                                      }>
                                        {ride.status}
                                      </Badge>
                                      {ride.is_paid && (
                                        <Badge className="bg-green-100 text-green-800">
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                          Paid
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-gray-600">
                                      {ride.customer_name || 'N/A'} • {ride.pickup_location || 'N/A'} → {ride.dropoff_location || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <div className="flex flex-col gap-1">
                                      <div>
                                        <span className="text-xs text-gray-500">Customer Price:</span>
                                        <span className="ml-1 font-medium">{formatCurrency(ride.price)}</span>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">Partner Payment:</span>
                                        <span className="ml-1 font-bold text-sky-600">
                                          {formatCurrency(ride.payment_to_partner || 0)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowPaymentModal(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lump Sum Payment Modal */}
      <Dialog open={showLumpSumModal} onOpenChange={(o) => { setShowLumpSumModal(o); if (!o) setEditingLumpSum(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="lump-sum-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-violet-600" />
              Lump Sum Payment — {selectedPartner?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            {/* Add new form */}
            <div className="border border-violet-200 bg-violet-50 rounded-sm p-3 space-y-3">
              <p className="text-xs font-semibold text-violet-900">Add new payment</p>
              <p className="text-xs text-slate-600">
                The oldest unpaid rides are marked as paid one-by-one until the next ride would exceed the amount.
                Any leftover becomes <b>credit</b> applied to future payments.
              </p>
              {partnerFinance.credit_balance > 0 && (
                <div className="text-xs bg-white border border-violet-300 rounded px-2 py-1.5 text-violet-800">
                  Existing credit: <strong>{formatCurrency(partnerFinance.credit_balance)}</strong> will be combined with this payment.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Amount (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={lumpSumAmount}
                    onChange={(e) => setLumpSumAmount(e.target.value)}
                    placeholder="3000.00"
                    className="mt-1"
                    data-testid="lump-sum-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Notes (optional)</Label>
                  <Input
                    value={lumpSumNotes}
                    onChange={(e) => setLumpSumNotes(e.target.value)}
                    placeholder="Bank transfer / cash / etc."
                    className="mt-1"
                    data-testid="lump-sum-notes"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={submitLumpSum}
                  disabled={submittingLumpSum || !lumpSumAmount}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="lump-sum-submit"
                >
                  {submittingLumpSum ? 'Processing…' : 'Confirm Payment'}
                </Button>
              </div>
            </div>

            {/* History list */}
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">
                History ({lumpSumHistory.length})
              </p>
              {loadingLumpSums ? (
                <div className="text-center py-6 text-slate-500 text-sm">Loading…</div>
              ) : lumpSumHistory.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">No lump-sum payments yet.</div>
              ) : (
                <div className="space-y-2">
                  {lumpSumHistory.map((ls) => {
                    const isEdit = editingLumpSum?.id === ls.id;
                    return (
                      <div
                        key={ls.id}
                        className="border border-slate-200 rounded p-2.5"
                        data-testid={`lump-sum-row-${ls.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-base font-bold text-violet-700">{formatCurrency(ls.amount_input)}</span>
                              <span className="text-xs text-slate-500">{ls.uploaded_at ? new Date(ls.uploaded_at).toLocaleString() : ''}</span>
                              {ls.uploaded_by_name && (
                                <span className="text-xs text-slate-400">by {ls.uploaded_by_name}</span>
                              )}
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5 space-x-2">
                              <span>Paid <b>{ls.ride_ids?.length || 0}</b> ride(s) = {formatCurrency(ls.rides_paid_total)}</span>
                              {ls.credit_used > 0 && (
                                <span className="text-violet-700">+ credit used {formatCurrency(ls.credit_used)}</span>
                              )}
                              {ls.credit_left > 0 && (
                                <span className="text-violet-700">→ credit saved {formatCurrency(ls.credit_left)}</span>
                              )}
                            </div>
                            {!isEdit && ls.notes && (
                              <div className="text-xs text-slate-500 italic mt-1">"{ls.notes}"</div>
                            )}
                            {isEdit && (
                              <div className="mt-2 flex items-center gap-2">
                                <Input
                                  value={editingLumpSum.notes || ''}
                                  onChange={(e) => setEditingLumpSum({ ...editingLumpSum, notes: e.target.value })}
                                  placeholder="Notes…"
                                  className="text-xs h-8"
                                  data-testid={`edit-lump-sum-notes-${ls.id}`}
                                />
                                <Button size="sm" className="h-8" onClick={saveLumpSumNotes} data-testid={`save-lump-sum-notes-${ls.id}`}>
                                  Save
                                </Button>
                                <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingLumpSum(null)}>
                                  Cancel
                                </Button>
                              </div>
                            )}
                          </div>
                          {!isEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => setEditingLumpSum({ id: ls.id, notes: ls.notes || '' })}
                                title="Edit notes"
                                data-testid={`edit-lump-sum-${ls.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
                                onClick={() => deleteLumpSum(ls)}
                                title="Delete payment (revert)"
                                data-testid={`delete-lump-sum-${ls.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLumpSumModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complaints Modal */}
      <Dialog open={showComplaintsModal} onOpenChange={(o) => { setShowComplaintsModal(o); if (!o) { setEditingComplaint(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="complaints-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-rose-600" />
              Complaints — {selectedPartner?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-2">
            {/* Add / Edit form */}
            <div className="border border-slate-200 rounded p-3 bg-slate-50">
              <p className="text-xs font-semibold text-slate-700 mb-2">
                {editingComplaint ? 'Edit complaint' : 'Add new complaint'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={complaintForm.description}
                    onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                    placeholder="What happened?"
                    className="mt-1"
                    data-testid="complaint-description"
                  />
                </div>
                <div>
                  <Label className="text-xs">Amount (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={complaintForm.amount}
                    onChange={(e) => setComplaintForm({ ...complaintForm, amount: e.target.value })}
                    placeholder="0.00"
                    className="mt-1"
                    data-testid="complaint-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={complaintForm.date}
                    onChange={(e) => setComplaintForm({ ...complaintForm, date: e.target.value })}
                    className="mt-1"
                    data-testid="complaint-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Ride ID (optional)</Label>
                  <Input
                    value={complaintForm.ride_id}
                    onChange={(e) => setComplaintForm({ ...complaintForm, ride_id: e.target.value })}
                    placeholder=""
                    className="mt-1"
                    data-testid="complaint-ride-id"
                  />
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={complaintForm.status} onValueChange={(v) => setComplaintForm({ ...complaintForm, status: v })}>
                    <SelectTrigger className="mt-1" data-testid="complaint-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                {editingComplaint && (
                  <Button size="sm" variant="ghost" onClick={() => openComplaintForm(null)}>
                    Cancel edit
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={saveComplaint}
                  disabled={savingComplaint}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="save-complaint-btn"
                >
                  {savingComplaint ? 'Saving…' : (editingComplaint ? 'Update' : 'Add Complaint')}
                </Button>
              </div>
            </div>

            {/* Complaints list */}
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">
                {complaints.length} complaint{complaints.length !== 1 ? 's' : ''} —
                <span className="ml-1">
                  deducted {monthFilter !== 'all' ? `for ${monthLabel(monthFilter)}` : 'from unpaid'}:{' '}
                  <strong className="text-rose-700">{formatCurrency(filteredComplaintsTotal)}</strong>
                </span>
                {monthFilter !== 'all' && partnerFinance.complaints_total > filteredComplaintsTotal && (
                  <span className="ml-1 text-slate-400">
                    · all months: {formatCurrency(partnerFinance.complaints_total)}
                  </span>
                )}
              </p>
              {loadingComplaints ? (
                <div className="text-center py-6 text-slate-500 text-sm">Loading…</div>
              ) : complaints.length === 0 ? (
                <div className="text-center py-6 text-slate-400 text-sm">No complaints yet.</div>
              ) : (
                <div className="space-y-2">
                  {complaints.map((c) => (
                    <div
                      key={c.id}
                      className="border border-slate-200 rounded p-2.5 flex items-start justify-between gap-3"
                      data-testid={`complaint-row-${c.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{c.description}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                          <span>{c.date}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            c.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                            c.status === 'resolved' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-200 text-slate-700'
                          }`}>{c.status}</span>
                          {c.ride_id && <span className="text-slate-400">ride: {c.ride_id.slice(0, 8)}…</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-rose-700 font-semibold text-sm">−{formatCurrency(c.amount)}</span>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openComplaintForm(c)} data-testid={`edit-complaint-${c.id}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => deleteComplaint(c)} data-testid={`delete-complaint-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplaintsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Mark as Paid Modal with Receipt Upload */}
      <Dialog open={showMarkPaidModal} onOpenChange={setShowMarkPaidModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-600" />
              Mark Rides as Paid
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <p className="text-sm text-green-800">
                <strong>{selectedRidesForPayment.length}</strong> ride(s) selected
              </p>
              <p className="text-lg font-bold text-green-700 mt-1">
                Total: {formatCurrency(getSelectedRidesTotal())}
              </p>
            </div>

            {/* Receipt File Upload */}
            <div>
              <Label htmlFor="receipt" className="text-sm font-medium mb-2 block">
                Receipt/Proof of Payment (Optional)
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-sm p-4 text-center hover:border-green-400 transition-colors">
                <input
                  type="file"
                  id="receipt"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="hidden"
                  data-testid="receipt-file-input"
                />
                <label htmlFor="receipt" className="cursor-pointer">
                  {receiptFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <FileText className="h-5 w-5" />
                      <span className="text-sm font-medium">{receiptFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <Upload className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Click to upload receipt (optional)</p>
                      <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG, WEBP (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="payment-notes" className="text-sm font-medium mb-2 block">
                Notes (optional)
              </Label>
              <Input
                id="payment-notes"
                placeholder="e.g., Bank transfer reference, payment date..."
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowMarkPaidModal(false);
                setReceiptFile(null);
                setPaymentNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkRidesAsPaid}
              disabled={markingPaid || selectedRidesForPayment.length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-mark-paid-btn"
            >
              {markingPaid ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Paid
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Uploaded Files Modal */}
      <Dialog open={showInvoicesModal} onOpenChange={setShowInvoicesModal}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-emerald-500" />
              Files Uploaded by {selectedPartnerForInvoices?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingInvoices ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : partnerInvoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FolderOpen className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No files uploaded by this partner yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                {partnerInvoices.length} file(s) uploaded
              </p>
              
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">File Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rides</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {partnerInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {(invoice.uploaded_at || invoice.created_at) ? new Date(invoice.uploaded_at || invoice.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          }) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            <span className="truncate max-w-[200px]" title={invoice.file_name}>
                              {invoice.file_name || 'Invoice'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-700">
                          €{(invoice.total_amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700">
                          {invoice.ride_ids?.length || 0} ride(s)
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            invoice.status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {(invoice.has_file || invoice.file_name) && (
                              <>
                                <button
                                  onClick={() => handlePreviewInvoice(invoice)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded"
                                  title="View receipt"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDownloadInvoice(invoice)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-sky-600 hover:text-sky-800 hover:bg-sky-50 rounded"
                                  title="Download receipt"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {invoice.has_settlement_excel && (
                              <button
                                onClick={() => handleDownloadSettlementExcel(invoice)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:text-white hover:bg-emerald-600 border border-emerald-300 rounded"
                                title="Download Settlement Excel (auto-generated)"
                                data-testid={`download-settlement-excel-${invoice.id}`}
                              >
                                <FileText className="h-4 w-4" />
                                <span>Excel</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Notes if any */}
              {partnerInvoices.some(inv => inv.notes) && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Invoice Notes:</h4>
                  {partnerInvoices.filter(inv => inv.notes).map((invoice) => (
                    <div key={invoice.id} className="text-sm text-slate-600 mb-2">
                      <span className="font-medium">{invoice.file_name}:</span> {invoice.notes}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInvoicesModal(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
